import { currentDirection } from "@app/lib/helper";
import { getTabProjects, projectTabs } from "@modules/investor-relations/data";
import { useMarqueeScroll } from "@modules/investor-relations/hooks/useMarqueeScroll";
import { trans } from "@mongez/localization";
import { useState } from "react";
import { ProjectCard } from "./ProjectCard";
import { ProjectTabs } from "./ProjectTabs";

/**
 * Project Highlights — Section 09 of the approved IR content sheet.
 *
 * Tabs carry the sector labels shown in the approved design; each maps 1:1 to
 * the subsidiary project selection in the content sheet.
 *
 * The strip drifts continuously rather than stepping between slides, so it is
 * a scroll container driven by `useMarqueeScroll` instead of a Swiper — see
 * that hook for why. The list is rendered twice to give the wrap something to
 * land on.
 */
export function ProjectHighlights() {
  const dir = currentDirection();
  const [activeTab, setActiveTab] = useState(projectTabs[0].key);

  const projects = getTabProjects(activeTab);
  // const { sectorSlug } = getProjectTab(activeTab);

  const { trackRef, trackProps } = useMarqueeScroll({
    rtl: dir === "rtl",
    // A tab switch changes the list, so the drift restarts at the first card.
    resetKey: activeTab,
  });

  return (
    <section
      aria-labelledby="project-highlights-title"
      className="pb-14 lg:pb-16"
    >
      <div className="container flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-4">
          <h2
            id="project-highlights-title"
            className="text-2xl font-bold text-marine sm:text-3xl"
          >
            {trans("projectHighlights.title")}
          </h2>

          <ProjectTabs activeTab={activeTab} onChange={setActiveTab} />

          {/* There is no all-projects page, so the "All" tab shows no link;
              every sector tab points at its own sector page instead. */}
          <div className="min-w-32">
            {/* {sectorSlug && (
              <Link
                to={URLS.sectors.view(sectorSlug)}
                className="flex shrink-0 items-center gap-2 text-sm font-bold text-secondary transition-colors hover:text-marine"
              >
                {trans("projectHighlights.viewAll")}
                <ChevronRight
                  className="size-4 rtl:rotate-180"
                  strokeWidth={1.5}
                  aria-hidden
                />
              </Link>
            )} */}
          </div>
        </div>

        <div
          ref={trackRef}
          {...trackProps}
          // Keyed on the tab so a switch remounts the cards, replaying their
          // fade-in, and so the duplicated pass is rebuilt with it.
          key={`${dir}-${activeTab}`}
          tabIndex={0}
          role="group"
          aria-label={trans("projectHighlights.title")}
          className="flex cursor-grab gap-4 overflow-x-auto overscroll-x-contain no-scrollbar focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary active:cursor-grabbing"
        >
          {/* Two passes of the same list. The second is decorative — the wrap
              target — so it stays out of the accessibility tree. */}
          {[false, true].map((isDuplicate) =>
            projects.map((project, index) => (
              <div
                key={`${isDuplicate ? "echo" : "lead"}-${project.key}`}
                aria-hidden={isDuplicate || undefined}
                className="w-[85%] shrink-0 xs:w-[60%] sm:w-[44%] lg:w-[30%] xl:w-[22%] 2xl:w-[19%]"
              >
                <ProjectCard project={project} index={index} />
              </div>
            )),
          )}
        </div>
      </div>
    </section>
  );
}
